<template>
  <Dialog
    :open="open"
    :close-on-overlay-click="false"
    content-class="w-90"
    @update:open="onOpenChange"
  >
    <template #header>
      <h2 class="text-2xl-medium text-black-white">{{ title }}</h2>
    </template>

    <div class="text-m-medium">
      <template v-if="request.kind === 'confirmEnterBoot'">
        <p>
          {{ t('setting.device.firmware.boot.confirmEnterBoot.body1Prefix') }}
          <span class="text-text-blue-active"> {{ displayName }} </span>
          {{ t('setting.device.firmware.boot.confirmEnterBoot.body1Suffix') }}
        </p>
        <p>{{ t('setting.device.firmware.boot.confirmEnterBoot.body2') }}</p>
        <p>{{ t('setting.device.firmware.boot.confirmEnterBoot.body3') }}</p>
      </template>
      <template v-else>
        <p>
          {{ t('setting.device.firmware.boot.authorizeBootDevice.body1Prefix') }}
          <span class="text-text-blue-active">{{ displayName }}</span>
        </p>
        <p>{{ t('setting.device.firmware.boot.authorizeBootDevice.body2') }}</p>
        <p>{{ t('setting.device.firmware.boot.authorizeBootDevice.body3') }}</p>
      </template>
    </div>

    <template #footer>
      <div class="flex w-full gap-3">
        <Button v-if="allowCancel" class="flex-1" color="text-b-w" @click="onCancel">
          {{ t('common.cancel') }}
        </Button>
        <Button class="flex-1" color="black" @click="onConfirm">{{ confirmLabel }}</Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Button, Dialog } from '@rapoo/element'
  import type { FirmwareAuthPromptRequest } from '@rapoo/device-sdk'

  const props = defineProps<{
    request: FirmwareAuthPromptRequest
  }>()

  const emit = defineEmits<{
    confirm: []
    cancel: []
  }>()

  const { t } = useI18n()
  const open = ref(true)
  const allowCancel = computed(() => props.request.allowCancel)

  /** 蓝色提示：系统选择器中可能出现的真实产品名示意，绝不带出 VID/PID。 */
  const displayName = computed(() => {
    const fallback = t('setting.device.firmware.boot.defaultUpgraderName')
    const raw = props.request.boot.label?.trim()
    if (!raw) return fallback
    return raw.replace(/\s*\(?\s*0x[\da-fA-F]+\s*:\s*0x[\da-fA-F]+\s*\)?/g, '').trim() || fallback
  })

  const title = computed(() =>
    props.request.kind === 'confirmEnterBoot'
      ? t('setting.device.firmware.boot.confirmEnterBoot.title')
      : t('setting.device.firmware.boot.authorizeBootDevice.title'),
  )

  const confirmLabel = computed(() =>
    props.request.kind === 'confirmEnterBoot'
      ? t('setting.device.firmware.boot.confirmEnterBoot.confirm')
      : t('setting.device.firmware.boot.authorizeBootDevice.confirm'),
  )

  function onConfirm() {
    open.value = false
    emit('confirm')
  }

  function onCancel() {
    if (!allowCancel.value) return
    open.value = false
    emit('cancel')
  }

  function onOpenChange(next: boolean) {
    if (next) return
    if (allowCancel.value) {
      emit('cancel')
      return
    }
    open.value = true
  }
</script>
